import { Component, Input, OnInit, inject } from '@angular/core';
import { CommonModule } from '@angular/common';
import { SharedModule } from '../shared.module';
import { TMSService } from '../../core/service/tms/tms.service';
import { LocalStorageService } from '../../core/service/localStorage/local-storage.service';
import moment from 'moment';

// Same "done" statuses sign-off-wrapup uses to decide a task needs no more attention.
const DONE_STATUSES = ['resolved', 'closed', 'completed', 'resolved_not_reviewed'];

interface OverviewTask {
  id: string;
  taskNumber: string;
  name: string;
  assignedBy: string;
  dueLabel: string;
  completed: boolean;
}

interface OverviewCalendarEvent {
  id: string;
  name: string;
  time: string;
}

// Read-only reminder of today's tasks/calendar — reuses the same TMSService
// endpoints as sign-off-wrapup, but with no attendance/completion editing,
// so it can be dropped into the away-status popup and a post-login dialog.
@Component({
  selector: 'app-today-overview',
  standalone: true,
  imports: [CommonModule, SharedModule],
  templateUrl: './today-overview.component.html',
  styleUrl: './today-overview.component.scss',
})
export class TodayOverviewComponent implements OnInit {
  private tmsService = inject(TMSService);
  private localStorageService = inject(LocalStorageService);

  // Dark variant for placement over the away-lock/away-status dark backdrop;
  // default (light) suits dialogs on a white surface.
  @Input() dark = false;

  tasks: OverviewTask[] = [];
  tasksLoading = true;
  calendarEvents: OverviewCalendarEvent[] = [];
  calendarLoading = true;

  async ngOnInit(): Promise<void> {
    const currentUser = await this.localStorageService.getKeyIndexedDbKey('currentUser');
    this._loadTasks(currentUser?.id);
    this._loadCalendarEvents();
  }

  get pendingTaskCount(): number {
    return this.tasks.filter(t => !t.completed).length;
  }

  private _loadTasks(userId: any): void {
    const today = moment().format('YYYY-MM-DD');
    this.tasksLoading = true;

    this.tmsService.getTasks(today, userId).subscribe({
      next: (res: any) => {
        this.tasksLoading = false;
        const data = res?.data?.data || [];
        this.tasks = data.map((item: any) => ({
          id: item?.id || '',
          taskNumber: item?.taskNumber ?? '',
          name: item?.title || 'Untitled task',
          assignedBy: item?.requesterName ? `Assigned by ${item.requesterName}` : 'Assigned by Self',
          dueLabel: item?.resolutionDueAt ? `Due ${moment(item.resolutionDueAt).format('hh:mm A')}` : 'Due Today',
          completed: DONE_STATUSES.includes(item?.status),
        }));
      },
      error: () => {
        this.tasksLoading = false;
      },
    });
  }

  private _loadCalendarEvents(): void {
    const today = moment().format('YYYY-MM-DD');
    this.calendarLoading = true;

    this.tmsService.getCalendarEvents(today, today).subscribe({
      next: (res: any) => {
        this.calendarLoading = false;
        const events = res?.data?.events || [];
        this.calendarEvents = events.map((item: any) => ({
          id: item?.id || '',
          name: item?.title || item?.summary || item?.name || '',
          time: this._formatEventTime(item),
        }));
      },
      error: () => {
        this.calendarLoading = false;
      },
    });
  }

  private _formatEventTime(item: any): string {
    const startRaw = item?.startTime || item?.start?.dateTime || item?.start;
    const endRaw = item?.endTime || item?.end?.dateTime || item?.end;
    if (!startRaw) return '';
    const start = moment(startRaw).format('hh:mm A');
    const end = endRaw ? moment(endRaw).format('hh:mm A') : '';
    return end ? `${start} – ${end}` : start;
  }
}
